import { isFuture } from 'date-fns';
import { BasePageModel } from 'models/BasePageModel';
import { BrandContentDisplayType } from 'models/BrandContentDisplayType';
import { ILocation } from 'models/ILocation';
import { PageState } from 'models/PageState';
import { isProductGuideBlock } from 'models/ProductGuideBlock';
import {
  isProductlistPage,
  isSearchProductlistPage,
  ProductlistBase,
  ProductlistPageModel
} from 'models/ProductlistPageModel';
import { ProductListType } from 'models/ProductListType';
import { SaleStatus } from 'models/SaleStatus';
import { SortOrder } from 'models/SortOrder';
import { createSelector } from 'reselect';
import { formatDateShort, rm } from 'utils';

export const isProductlist = (currentPage: BasePageModel): currentPage is ProductlistBase =>
  isSearchProductlistPage(currentPage) || isProductlistPage(currentPage);

const normalProductlistTypes = [
  ProductListType.Brand,
  ProductListType.Normal,
  ProductListType.BrandCategoryFilter,
  ProductListType.CategoryFilter
];

export const isNormalProductlistPage = (page: BasePageModel): page is ProductlistPageModel => {
  return isProductlistPage(page) && normalProductlistTypes.includes(page.productlistType);
};

export const hasBrandContent = (state: PageState) => {
  if (!isProductlistPage(state.currentPage)) {
    return false;
  }
  return state.currentPage.brandContent !== undefined;
};

const MIN_PRODUCTS_FOR_FILTER = 8;
export const showFilter = (currentPage: BasePageModel) => {
  if (!isProductlistPage(currentPage)) {
    return true;
  }

  return currentPage.totalNbrOfHits > MIN_PRODUCTS_FOR_FILTER;
};

export const getProductList = (currentPage: BasePageModel) =>
  isProductlist(currentPage) ? currentPage.products : [];

export const getIsCategoryFilter = (state: PageState) =>
  isProductlist(state.currentPage) ? state.currentPage.isCategoryFilterPage : false;

/**
 * CategoryFilter seems to trump BrandCategoryFilter
 */
export const getProductListType = createSelector(
  (state: PageState) =>
    isProductlist(state.currentPage) ? state.currentPage.productlistType : ProductListType.Normal,
  getIsCategoryFilter,
  (type, isCategoryFilter) => (isCategoryFilter ? ProductListType.CategoryFilter : type)
);

export const getIsProductlistCustomBrand = createSelector(
  getProductListType,
  type => type === ProductListType.Brand || type === ProductListType.BrandCategoryFilter
);
export const getSelectedSortOrder = (currentPage: BasePageModel) =>
  isProductlist(currentPage) ? currentPage.selectedSortOrder : SortOrder.RelevancyDesc;

export const getSortOrders = (currentPage: BasePageModel) =>
  isProductlist(currentPage) ? currentPage.sortOrders : [];

export const getPageNumber = (currentPage: BasePageModel) =>
  isProductlist(currentPage) ? currentPage.pageNbr : 1;

export const getPageUrlNumber = (location: ILocation): number =>
  location?.search?.page ? +location.search.page : 1;

export const getAction = (location: ILocation & { action?: string }): string =>
  location?.action ? location.action : '';

export const getNumberOfPages = (currentPage: BasePageModel) =>
  isProductlist(currentPage) ? currentPage.nbrOfPages : 1;

export const getSliderAds = (currentPage: BasePageModel) =>
  isNormalProductlistPage(currentPage) ? currentPage.sliderAds : undefined;

export const getCountdownAd = (currentPage: BasePageModel) =>
  isNormalProductlistPage(currentPage) ? currentPage.countdownAd : undefined;

export const getRootCategory = (currentPage: BasePageModel) =>
  isNormalProductlistPage(currentPage) ? currentPage.rootCategory : undefined;

export const getSeoBlock = (currentPage: BasePageModel) =>
  isNormalProductlistPage(currentPage) ? currentPage.seoBlock : undefined;

export const getExtendedDescription = (state: PageState) =>
  isNormalProductlistPage(state.currentPage) ? state.currentPage.seoBlock?.content : undefined;

export const getDescription = (currentPage: BasePageModel) =>
  isNormalProductlistPage(currentPage) ? currentPage.description : '';

export const getHeader = (state: PageState) =>
  isNormalProductlistPage(state.currentPage) ? state.currentPage.header : '';

export const getBrandContent = (currentPage: BasePageModel) =>
  isNormalProductlistPage(currentPage) ? currentPage.brandContent : undefined;

export const getBrandContentDisplayType = createSelector(getBrandContent, brandContent =>
  brandContent !== undefined ? brandContent.displayType : BrandContentDisplayType.Unspecified
);

export const getProductlistBase = (currentPage: BasePageModel) => {
  if (!isProductlist(currentPage)) {
    return {};
  }
  const { webCatalog, productlistType, totalNbrOfHits } = currentPage;
  return { webCatalog, productlistType, totalNbrOfHits };
};

export const selectWebCatalog = (currentPage: BasePageModel): string => {
  if (isNormalProductlistPage(currentPage)) {
    return currentPage.webCatalog;
  }
  return '';
};

export const getRootCategoryUrl = (page: BasePageModel) => {
  if (!isNormalProductlistPage(page)) {
    return '';
  }

  return page.rootCategory !== undefined ? page.rootCategory.url : ';';
};

export const getNumberOfHits = (currentPage: BasePageModel) =>
  isProductlist(currentPage) ? currentPage.nbrOfHits : 0;

export const getProductsPerPage = (currentPage: BasePageModel) =>
  isProductlist(currentPage) ? currentPage.productsPerPage : 0;

export const getCurrentCampaigns = (currentPage: BasePageModel) =>
  isProductlist(currentPage) ? (currentPage as ProductlistPageModel).currentCampaignAds : [];

export const getCategories = (currentPage: BasePageModel) => {
  if (!isProductlistPage(currentPage)) {
    return [];
  }
  return currentPage.categoryNavigationItems ?? [];
};

export const getHasCategories = (state: PageState) => {
  if (!isProductlistPage(state.currentPage)) {
    return false;
  }
  return state.currentPage.categoryNavigationItems === undefined
    ? false
    : state.currentPage.categoryNavigationItems.length > 0;
};

export const getFilteredCategories = (currentPage: BasePageModel) => {
  if (!isProductlistPage(currentPage)) {
    return [];
  }
  return currentPage.filterCategories ?? [];
};

export const getNumberOfFaqs = (currentPage: BasePageModel) =>
  isProductlist(currentPage) ? currentPage.totalNumberOfFrequentlyAskedQuestions : 0;

export const getContent = (currentPage: ProductlistPageModel) =>
  isProductlist(currentPage) ? currentPage.content : [];

export const isHero = (currentPage: ProductlistPageModel): boolean => {
  if (!isProductlistPage(currentPage)) {
    return false;
  }

  const productGuideBlock = currentPage?.content.find(isProductGuideBlock);
  if (!productGuideBlock) {
    return false;
  }

  return productGuideBlock.showHero;
};

const EARLIEST_YEAR = 1900;

export const deliveryStatusString = (
  stockSaleStatus = SaleStatus.NotDefined,
  saleStatusString: string,
  deliveryTime: string,
  publishedSaleDate = '',
  isNewProduct: boolean
): string => {
  if (stockSaleStatus === SaleStatus.Active) {
    return deliveryTime;
  } else {
    const date = new Date(publishedSaleDate);
    let result = saleStatusString;
    if (!isNaN(date.getFullYear()) && date.getFullYear() !== EARLIEST_YEAR) {
      result += `: ${formatDateShort(date)}`;
    }
    if (!isFuture(date) && isNewProduct) {
      result = rm('delivery_salestatus_notinstock_isnew_incoming');
    }
    return result;
  }
};

export const descriptionSelector = (state: PageState) => getDescription(state.currentPage);
